import React, { useMemo, useState } from "react";
import { Form, InputGroup, Modal } from "react-bootstrap";
import {
  ChevronDown,
  ChevronRight,
  Download,
  FileEarmarkBarGraph,
  Search,
  XLg,
} from "react-bootstrap-icons";
import { HeaderButton, SubmitButton, CancelButton } from "../../shared";
import { FormattedDate } from "../../../../lib/components/FormattedDate";

function getReportName(report) {
  return (
    report?.friendlyName ||
    report?.FriendlyName ||
    report?.reportName ||
    report?.name ||
    "Report"
  );
}

function getReportFileName(file) {
  return file?.name || file?.fileName || file?.FileName || "Report file";
}

function getReportMeta(report) {
  const fileCount =
    report?.fileCount ?? (report?.files ? report.files.length : null);
  const fileTypes = Array.isArray(report?.fileTypes)
    ? report.fileTypes.join(", ")
    : report?.fileTypes;
  return [
    report?.archiveFileName || null,
    report?.status || null,
    fileCount ? `${fileCount} files` : null,
    fileTypes || null,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function PayoutRunReportsModal({
  reports = [],
  runName,
  totalCount,
  onCancel,
  onRequestReport,
  onDownloadReport,
  onLoadMore,
}) {
  const [search, setSearch] = useState("");
  const [downloadingReferenceId, setDownloadingReferenceId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => {
    const initial = new Set();
    const firstWithFiles = reports.find((r) => (r?.files ?? []).length > 0);
    if (firstWithFiles) {
      const key =
        firstWithFiles.id ??
        firstWithFiles.reportId ??
        `${getReportName(firstWithFiles)}-0`;
      initial.add(key);
    }
    return initial;
  });

  const filteredReports = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return reports;
    return reports.filter((report) =>
      getReportName(report).toLowerCase().includes(query),
    );
  }, [reports, search]);

  const toggleExpanded = (key) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleDownloadReport = async (report) => {
    const referenceId = report?.referenceId ?? report?.ReferenceId;
    if (!referenceId || !onDownloadReport || downloadingReferenceId) return;

    setDownloadingReferenceId(referenceId);
    try {
      await onDownloadReport(report);
    } finally {
      setDownloadingReferenceId(null);
    }
  };

  const shownCount = filteredReports.length;
  const archiveCount = totalCount ?? reports.length;

  return (
    <Modal
      show
      onHide={onCancel}
      centered
      size="lg"
      className="commission-modal-scope payout-run-reports-modal"
    >
      <Modal.Header className="payout-run-reports-header">
        <div>
          <Modal.Title className="payout-run-reports-title">
            Reports
          </Modal.Title>
          <div className="payout-run-reports-subtitle">
            {runName ? `${runName} · ` : ""}
            {archiveCount} {archiveCount === 1 ? "archive" : "archives"}
          </div>
        </div>
        <div className="payout-run-reports-header-actions">
          {onRequestReport && (
            <HeaderButton
              text="Request report"
              variant="outline-primary"
              size="sm"
              icon={<FileEarmarkBarGraph size={14} />}
              onClick={onRequestReport}
            />
          )}
          <button
            type="button"
            className="payout-run-reports-close"
            aria-label="Close modal"
            onClick={onCancel}
          >
            <XLg size={15} />
          </button>
        </div>
      </Modal.Header>

      <div className="payout-run-reports-search-row">
        <InputGroup className="payout-run-reports-search">
          <InputGroup.Text>
            <Search size={13} />
          </InputGroup.Text>
          <Form.Control
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search reports"
          />
        </InputGroup>
      </div>

      <Modal.Body className="p-0">
        {filteredReports.length === 0 ? (
          <div className="payout-run-reports-empty">
            No reports match your search.
          </div>
        ) : (
          <div className="payout-run-reports-list">
            {filteredReports.map((report, index) => {
              const reportKey =
                report.id ??
                report.reportId ??
                `${getReportName(report)}-${index}`;
              const isExpanded = expandedIds.has(reportKey);
              const files = report?.files ?? [];
              const badgeType = report.badge
                ? String(report.badge).toLowerCase()
                : null;

              return (
                <div className="payout-run-report-group" key={reportKey}>
                  <div className="payout-run-report-row">
                    <button
                      type="button"
                      className="payout-run-report-toggle"
                      onClick={() => toggleExpanded(reportKey)}
                      disabled={files.length === 0}
                      aria-expanded={isExpanded}
                      aria-label={
                        isExpanded ? "Collapse details" : "Expand details"
                      }
                    >
                      {files.length > 0 ? (
                        isExpanded ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )
                      ) : (
                        <span className="payout-run-report-toggle-spacer" />
                      )}
                    </button>

                    <div className="payout-run-report-info">
                      <div className="payout-run-report-name">
                        <strong>{getReportName(report)}</strong>
                        {badgeType && (
                          <span
                            className={`payout-run-report-badge payout-run-report-badge--${badgeType}`}
                          >
                            {report.badge.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="payout-run-report-file-meta">
                        {getReportMeta(report)}
                      </div>
                    </div>

                    <div className="payout-run-report-date">
                      {report.createdAt && (
                        <FormattedDate
                          date={report.createdAt}
                          format="MM/DD h:mm A"
                        />
                      )}
                    </div>
                    <div className="payout-run-report-author">
                      {report.portalUserName || "—"}
                    </div>
                    {onDownloadReport && (
                      <SubmitButton
                        size="sm"
                        icon={<Download size={13} />}
                        text="Download"
                        onClick={() => handleDownloadReport(report)}
                        disabled={
                          !(
                            report?.referenceId ?? report?.ReferenceId
                          ) || downloadingReferenceId != null
                        }
                        aria-label={`Download ${getReportName(report)}`}
                      />
                    )}

                  </div>

                  {isExpanded && files.length > 0 && (
                    <div className="payout-run-report-files">
                      <div className="payout-run-report-files-label">
                        Included in this zip
                      </div>
                      <div className="payout-run-report-files-list">
                        {files.map((file) => (
                          <div
                            className="payout-run-report-file-row"
                            key={
                              file.id ??
                              file.commissionReportFileId ??
                              getReportFileName(file)
                            }
                          >
                            <span className="payout-run-report-file-name">
                              {getReportFileName(file)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="payout-run-reports-footer">
        <div className="payout-run-reports-footer-left">
          <span className="payout-run-reports-count">
            Showing {shownCount} of {archiveCount}
          </span>
          <button
            type="button"
            className="payout-run-reports-load-more"
            onClick={onLoadMore}
          >
            Load more
          </button>
        </div>
        <CancelButton text="Close" onClick={onCancel} size="sm" />
      </Modal.Footer>
    </Modal>
  );
}

export default PayoutRunReportsModal;
